import {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {has, isNumber, slice} from "lodash";
import {LAYOUT_CONSTANTS} from "../const";
import {ArticleEntity} from "../contract/article";
import {Breakpoint, Breakpoints, SectionDef, Template} from "../contract/template";
import {RootState} from "../redux/store";
import useCurrentRoute from "./useCurrentRoute";
import useMedia from "./useMedia";
import {INITIAL_ROUTE} from "./useRoutes";

const {CONTENT_BASE_WIDTH, GUTTER_SIZE} = LAYOUT_CONSTANTS;
const rest: SectionDef[] = [[Template.SMALL, -1]];

interface Section {
  template: string;
  columns: number;
  entries: ArticleEntity[];
}

type useTemplateDef = (breakpoints: Breakpoints) => {
  sections: Section[],
  maxColumns: number,
  containerStyles: {[key: string]: number|string},
  LAYOUT_CONSTANTS: any,
}

const useTemplate: useTemplateDef = (breakpoints) => {
  const pathname = useCurrentRoute();
  const {onMobile, onTablet} = useMedia();
  const {articles, articles_visible} = useSelector((state: RootState) => {
    const {page_size, routes} = state.application;
    const articles = Object.values(state.entities.articles.byId);
    const route = has(routes, pathname) ? routes[pathname] : INITIAL_ROUTE;
    const {articles: articles_count, articles_unavailable} = route;
    const remaining = (articles_count || []).length % page_size;

    const articles_offset = remaining
      ? ((articles_count || []).length + page_size) - ((articles_count || []).length % page_size)
      : (articles_count || []).length

    const final =
      (articles_offset - page_size) + ((articles_count || []).length % page_size) === ((articles_count || []).length);

    return {
      articles: (articles_count || []).map(id => articles.find(article => String(article.id) === id)),
      articles_visible: (articles_offset - page_size) < 0
        ? page_size // first page
        : (final && articles_unavailable)
          ? articles_count.length // last page
          : (articles_offset - page_size)
    }
  });
  const [columns, setColumns] = useState<number>(1);
  const [maxColumns, setMaxColumns] = useState<number>(1);
  const [sections, setSections] = useState<any[]>(null);
  const {[Breakpoint.MOBILE]: mobile, [Breakpoint.TABLET]: tablet, [Breakpoint.DESKTOP]: desktop} = breakpoints;

  useEffect(() => {
    const {sections, columns} = (!onMobile && !onTablet) ? desktop : onMobile ? mobile : (tablet || desktop);
    setColumns(columns);
    setSections(sections.concat(rest));
  }, [onMobile, onTablet, mobile, desktop, tablet]);

  const slices = useMemo(() => {
    if (!sections) return [];
    const entries = (!articles || !articles.length)
      ? [...Array(articles_visible).keys()].map(() => null)
      : articles;

    let all: ArticleEntity[] = [];
    const results: Section[] = [];
    (sections || []).map(([template = Template.SMALL, count = 0]: SectionDef, index) => {
      const final = index === sections.length - 1;
      const start = all.length;
      const end = final
        ? (articles_visible || entries.length)
        : isNumber(count)
          ? (all.length + count)
          : 0;
      if (columns > maxColumns) setMaxColumns(columns);

      const section = slice(entries, start, end);
      all = all.concat(section);
      results.push({
        template,
        columns: final ? 1 : columns,
        entries: section
      });
    });

    return results;
  }, [articles, articles_visible, sections]); // eslint-disable-line react-hooks/exhaustive-deps

  const COLUMN_COUNT = onMobile ? 1 : (columns < 3) ? 3 : columns;
  const CONTENT_MAX_WIDTH = onMobile ? null : (COLUMN_COUNT * CONTENT_BASE_WIDTH) + (COLUMN_COUNT - 1) * GUTTER_SIZE;

  const containerStyles: {[key: string]: number|string} = {width: '100%'};
  if (onMobile) {
    containerStyles.overflowX = 'hidden';
  } else {
    containerStyles.maxWidth = `${CONTENT_MAX_WIDTH}px`;
  }

  return {
    sections: slices,
    maxColumns,
    containerStyles,
    LAYOUT_CONSTANTS: {
      ...LAYOUT_CONSTANTS,
      COLUMN_COUNT,
      CONTENT_MAX_WIDTH,
    }
  }
}

export default useTemplate;
